import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import {
    ApiFragment,
    Family,
    Mh2Entity,
    NavigationLink,
    RecordType,
    RecordTypeStructure
} from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ActionService } from '../../abstract/action.service.interface';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { IntellectualRecordsService } from '@common/services/intellectual-records/intellectual-records.service';

@Injectable({ providedIn: 'root' })
export class SetCoverActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.SET_AS_COLLECTION_COVER;

    // Services
    private readonly dialog: DialogHelper = inject(DialogHelper);
    private readonly intellectualRecordsService: IntellectualRecordsService = inject(IntellectualRecordsService);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Set cover
     * Available when:
     *  * User has edit rights on parent
     *  * Record is in context of record as full child or candidate child
     *  * Parent is of type Intellectual or Data
     *  * It or parent is not deleted
     *  * Record is not a Representation
     *
     * Disabled when:
     *  * Record is already set as the cover
     *  * Record doesn't have a preview
     */
    public getUserAction(target: ApiFragment, actionItemOptions: ActionItemOptions): Observable<UserAction> {
        return of(this.userActionBuilder(target, actionItemOptions));
    }

    public userActionBuilder(record: ApiFragment, actionItemOptions: ActionItemOptions): UserAction {
        const parent: ApiFragment | undefined = actionItemOptions.parent;

        const hasEditRights: boolean = !!parent && parent.hasEditRights();
        const isInParentContext: boolean =
            record.getParentRecordId() === parent?.getRecordId() ||
            record.getCandidateParentId() === parent?.getFragmentId();
        const isIntellectualOrDataObject: boolean =
            parent?.getRecordStructure() === RecordTypeStructure.Intellectual ||
            parent?.getRecordStructure() === RecordTypeStructure.Data;
        const isRepresentation: boolean = record.getRecordType() === RecordType.Representation;

        const isDeleted: boolean = record.isDeleted() || !!parent?.isDeleted();

        const isParentCover: boolean =
            !!parent && record.getRecordId() === parent.get(Family.Structural, 'PreviewRecordId');
        const hasPreview: boolean = record.hasPreview();

        const availableConditions: boolean[] = [
            isInParentContext,
            hasEditRights,
            isIntellectualOrDataObject,
            !isDeleted,
            !isRepresentation
        ];

        const disableConditions: ActionDisabledCondition[] = [
            { condition: isParentCover, tooltip: 'set-as-cover.is-current-cover' },
            { condition: !hasPreview, tooltip: 'general.no-preview' }
        ];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        const message: string = this.translate('collections.set-as-cover-dialog.message');
        const title: string = this.translate('actions.set-as-cover');
        const yes: string = this.translate('actions.set-as-cover');
        const no: string = this.translate('actions.common.cancel');

        return this.dialog.confirm({ title, message, yes, no }).pipe(
            tap(() => {
                if (!actionItemOptions.parent) {
                    console.error('Parent not set for action.');
                }
            }),
            tap(() => this.initBusyToast(record, actionItemOptions, true)),
            switchMap(confirm =>
                this.intellectualRecordsService.setAsCover(record, actionItemOptions.parent!).pipe(take(1))
            ),
            map(() => true)
        );
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.set-as-cover';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.set-as-cover';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public getSuccessToastLink(target: ApiFragment, actionItemOptions: ActionItemOptions): NavigationLink | undefined {
        return undefined;
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
